import React, { useState, useEffect } from "react";
import { API_URL } from '../config';
import BitrixAPI from "../api/b24";
import { FilesContainer } from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import PrintButton from "../components/PrintButton";
import { useHeader } from "../contexts/HeaderContext";

const DrillingOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [error, setError] = useState(null);
  
  // Состояние для фильтра просмотра
  const [viewFilter, setViewFilter] = useState("orders"); // "orders" или "details"
  
  // Состояния для деталировки всех заказов (как в CuttingOrdersPageB24)
  const [ordersDetails, setOrdersDetails] = useState({}); // {orderId: {glassDetails: [], selectedDetails: Set(), quantities: Map()}}
  const [ordersComments, setOrdersComments] = useState({}); // {orderId: "comments"}
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // ID заказов, для которых сейчас загружаются детали

  // Состояния для отслеживания статуса сохранения деталей (для каждой детали отдельно)
  const [detailSavingStatus, setDetailSavingStatus] = useState({}); // {`${orderId}_${detailId}`: 'idle' | 'saving' | 'saved' | 'error'}

  // Таймеры для debounce автосохранения (для каждой детали отдельно)
  const saveTimersRef = React.useRef({}); // {`${orderId}_${detailId}`: timeoutId}

  // Состояния для поиска
  const [searchTerm, setSearchTerm] = useState(''); // Поисковый запрос

  const { setHeaderExtras } = useHeader();

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}

  // Компонент индикатора статуса сохранения (для конкретной детали)
  const SaveStatusIndicator = ({ orderId, detailId }) => {
    const detailKey = `${orderId}_${detailId}`;
    const status = detailSavingStatus[detailKey] || 'idle';

    if (status === 'idle') {
      return null;
    }

    if (status === 'saving') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
          <svg className="animate-spin -ml-1 mr-1.5 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Сохранение...
        </span>
      );
    }

    if (status === 'saved') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
          <svg className="mr-1.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
          Сохранено
        </span>
      );
    }

    if (status === 'error') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
          <svg className="mr-1.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          Ошибка
        </span>
      );
    }

    return null;
  };

  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : 'bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      console.log(`🔄 Начинаем завершение заказа ${orderId}`);
      
      // Сначала сохраняем выбранные детали
      console.log(`💾 Шаг 1: Сохраняем выбранные детали для заказа ${orderId}`);
      await saveSelectedDetails(orderId);
      
      // Затем переводим заказ на следующую стадию
      console.log(`⏭️ Шаг 2: Переводим заказ ${orderId} на следующую стадию`);
      const result = await BitrixAPI.updateStagedrilling(orderId);
      
      console.log('Результат обновления стадии:', result);

      if (result && result.success) {
        setOrders(prevOrders => 
          prevOrders.filter(order => order.id !== orderId)
        );
        console.log(`✅ Заказ ${orderId} успешно завершен: детали сохранены и заказ переведен на следующую стадию`);
      } else {
        throw new Error(result?.message || 'Не удалось обновить стадию');
      }

    } catch (error) {
      console.error('Ошибка при обновлении стадии:', error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };


  // Функция загрузки деталей для заказа
  const loadOrderDetails = async (orderId) => {
    try {
      console.log(`🔄 Начинаем загрузку деталей для заказа ${orderId}`);
      const token = localStorage.getItem('token');
      
      const params = { 
        stage: 'drilling'
      };
      
      const response = await fetch(`${API_URL}/api/glass-details-fixed/${orderId}?stage=${params.stage}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.glassDetails) {
        // Обрабатываем сохраненное состояние - может быть JSON или простой текст
        let savedSelected = [];
        let savedQuantities = {};
        
        if (data.savedState?.selected) {
          if (data.savedState.selected.startsWith('[') || data.savedState.selected.startsWith('{')) {
            try {
              const parsed = JSON.parse(data.savedState.selected);
              if (Array.isArray(parsed)) {
                savedSelected = parsed;
              } else if (parsed && parsed.selectedDetails && Array.isArray(parsed.selectedDetails)) {
                savedSelected = parsed.selectedDetails.map(detail => detail.id);
              }
            } catch (e) {
              console.error('Ошибка парсинга сохраненных деталей:', e);
            }
          }
        }
        
        if (data.savedState?.quantities) {
          try {
            savedQuantities = JSON.parse(data.savedState.quantities);
          } catch (e) {
            console.error('Ошибка парсинга количеств:', e);
          }
        }
        
        setOrdersDetails(prev => ({
          ...prev,
          [orderId]: {
            glassDetails: data.glassDetails,
            selectedDetails: new Set(savedSelected),
            quantities: new Map(Object.entries(savedQuantities).map(([k, v]) => [k, parseInt(v) || 1]))
          }
        }));
        
        if (data.savedState?.comments) {
          setOrdersComments(prev => ({
            ...prev,
            [orderId]: data.savedState.comments
          }));
        }
        
        console.log(`✅ Детали успешно загружены для заказа ${orderId}`);
      }
    } catch (error) {
      console.error(`❌ Ошибка загрузки деталировки для заказа ${orderId}:`, error);
    }
  };

  // Автосохранение состояния детали на сервере (для конкретной детали)
  const autoSaveDetailState = async (orderId, detailId) => {
    const detailKey = `${orderId}_${detailId}`;

    try {
      // Получаем ТЕКУЩЕЕ состояние заказа из state
      setOrdersDetails(currentOrders => {
        const orderData = currentOrders[orderId];
        if (!orderData) {
          console.log(`⚠️ Нет данных для заказа ${orderId}, пропускаем сохранение`);
          return currentOrders;
        }

        // Устанавливаем статус "сохраняется" для конкретной детали
        setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'saving' }));

        const selectedArray = Array.from(orderData.selectedDetails);
        const quantitiesObj = Object.fromEntries(orderData.quantities);
        const comments = ordersComments[orderId] || '';

        console.log(`💾 Отправляем на сервер для заказа ${orderId}, деталь ${detailId}:`, {
          selectedDetails: selectedArray,
          selectedQuantities: quantitiesObj,
          totalGlassDetails: orderData.glassDetails?.length || 0
        });

        const token = localStorage.getItem('token');

        // Выполняем запрос
        fetch(`${API_URL}/api/glass-details/${orderId}/save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            stage: 'drilling',
            selectedDetails: selectedArray,
            selectedQuantities: quantitiesObj,
            comments: comments,
            glassDetails: orderData.glassDetails || [],
            autoSave: true
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Ошибка сохранения');
          }
          return response.json();
        })
        .then(data => {
          // Устанавливаем статус "сохранено" для конкретной детали
          setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'saved' }));
          console.log(`✅ Деталь ${detailId} заказа ${orderId} сохранена на сервере`);

          // Через 2 секунды возвращаем статус в idle
          setTimeout(() => {
            setDetailSavingStatus(prev => {
              const newState = { ...prev };
              delete newState[detailKey];
              return newState;
            });
          }, 2000);
        })
        .catch(error => {
          console.error(`❌ Ошибка сохранения детали ${detailId} заказа ${orderId}:`, error);
          setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'error' }));

          // Через 3 секунды возвращаем статус в idle
          setTimeout(() => {
            setDetailSavingStatus(prev => {
              const newState = { ...prev };
              delete newState[detailKey];
              return newState;
            });
          }, 3000);
        });

        return currentOrders; // Возвращаем неизменное состояние
      });
    } catch (error) {
      console.error(`❌ Критическая ошибка автосохранения для детали ${detailId} заказа ${orderId}:`, error);
      setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'error' }));

      setTimeout(() => {
        setDetailSavingStatus(prev => {
          const newState = { ...prev };
          delete newState[detailKey];
          return newState;
        });
      }, 3000);
    }
  };

  // Обработка изменения чекбокса детали с автосохранением
  const handleDetailToggle = (orderId, detailId, detail) => {
    const detailKey = `${orderId}_${detailId}`;

    setOrdersDetails(prev => {
      const orderData = prev[orderId] || { glassDetails: [], selectedDetails: new Set(), quantities: new Map() };
      const newSelectedDetails = new Set(orderData.selectedDetails);
      const newQuantities = new Map(orderData.quantities);

      if (newSelectedDetails.has(detailId)) {
        newSelectedDetails.delete(detailId);
        newQuantities.delete(detailId);
      } else {
        newSelectedDetails.add(detailId);
        newQuantities.set(detailId, detail.quantity || 1);
      }

      const updatedOrderData = {
        ...orderData,
        selectedDetails: newSelectedDetails,
        quantities: newQuantities
      };

      // Отменяем предыдущий таймер автосохранения для этой конкретной детали
      if (saveTimersRef.current[detailKey]) {
        clearTimeout(saveTimersRef.current[detailKey]);
      }

      // Автосохранение с debounce для конкретной детали
      saveTimersRef.current[detailKey] = setTimeout(() => {
        console.log(`🔄 Автосохранение детали ${detailId} заказа ${orderId}`);
        autoSaveDetailState(orderId, detailId);
        delete saveTimersRef.current[detailKey];
      }, 300);

      return {
        ...prev,
        [orderId]: updatedOrderData
      };
    });
  };

  // Обработка изменения количества с автосохранением
  const handleQuantityChange = (orderId, detailId, newQuantity, maxQuantity) => {
    if (newQuantity < 1 || newQuantity > maxQuantity) return;

    const detailKey = `${orderId}_${detailId}`;

    setOrdersDetails(prev => {
      const orderData = prev[orderId] || { glassDetails: [], selectedDetails: new Set(), quantities: new Map() };
      const newQuantities = new Map(orderData.quantities);
      newQuantities.set(detailId, newQuantity);

      const updatedOrderData = {
        ...orderData,
        quantities: newQuantities
      };

      // Отменяем предыдущий таймер автосохранения для этой детали
      if (saveTimersRef.current[detailKey]) {
        clearTimeout(saveTimersRef.current[detailKey]);
      }

      // Автосохранение с debounce для конкретной детали
      saveTimersRef.current[detailKey] = setTimeout(() => {
        console.log(`🔄 Автосохранение изменения количества для детали ${detailId} заказа ${orderId}`);
        autoSaveDetailState(orderId, detailId);
        delete saveTimersRef.current[detailKey];
      }, 500);

      return {
        ...prev,
        [orderId]: updatedOrderData
      };
    });
  };

  // Функция сохранения выбранных деталей
  const saveSelectedDetails = async (orderId) => {
    try {
      const orderData = ordersDetails[orderId];
      if (!orderData) {
        console.log(`Нет данных деталировки для заказа ${orderId}, пропускаем сохранение`);
        return true;
      }

      const selectedArray = Array.from(orderData.selectedDetails);
      const quantitiesObj = Object.fromEntries(orderData.quantities);
      const comments = ordersComments[orderId] || '';

      console.log(`💾 Сохраняем детали для заказа ${orderId}:`, {
        selected: selectedArray,
        quantities: quantitiesObj,
        comments
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/glass-details/${orderId}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stage: 'drilling',
          selectedDetails: selectedArray,
          selectedQuantities: quantitiesObj,
          comments: comments,
          glassDetails: orderData.glassDetails || []
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        console.log(`✅ Детали для заказа ${orderId} успешно сохранены`);
        return true;
      } else {
        throw new Error(result.message || 'Не удалось сохранить детали');
      }
    } catch (error) {
      console.error(`❌ Ошибка сохранения деталей для заказа ${orderId}:`, error);
      throw error;
    }
  };


  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchOrders = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const response = await BitrixAPI.getActiveOrdersdrilling();

      if (response && Array.isArray(response)) {
        // Определяем новые заказы (которых еще нет в текущем списке)
        const newOrders = response.filter(newOrder =>
          !orders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0 && !isInitialLoad) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов:`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }

        setOrders(response || []);

        // Загружаем деталировку только для новых заказов или всех при первой загрузке
        const ordersToLoadDetails = isInitialLoad ? response : newOrders;

        if (ordersToLoadDetails.length > 0) {
          console.log('📋 Начинаем параллельную загрузку деталей для', ordersToLoadDetails.length, 'заказов...');
          const detailPromises = ordersToLoadDetails.map(async (order) => {
            try {
              await loadOrderDetails(order.id);
              return { orderId: order.id, success: true };
            } catch (detailError) {
              return { orderId: order.id, success: false, error: detailError.message };
            }
          });
          
          // Ждем завершения всех запросов
          const detailResults = await Promise.allSettled(detailPromises);
          const successCount = detailResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
          const errorCount = detailResults.length - successCount;
          
          console.log(`🎯 ИТОГ загрузки деталей: ${successCount} успешно, ${errorCount} с ошибками`);
        }
        
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{getFilteredOrders().length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, orders, ordersDetails]);

  // Первоначальная загрузка заказов
  useEffect(() => {
    fetchOrders(true);
  }, []);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive) {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log('🔄 Проверка новых заказов...');
      fetchOrders(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, orders]); // orders в зависимостях для сравнения с новыми


  // Заказы, доступные локации текущего пользователя:
  // цех "Маметовой, 3а к5" видит только детали 4мм, цех "Мамбетова 325а" - всё, кроме 4мм
  const LOCATION_THICKNESS_FILTERS = {
    'Маметовой, 3а к5': (thickness) => thickness === '4мм',
    'Мамбетова 325а': (thickness) => thickness !== '4мм'
  };

  const getLocationOrders = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};

    const matchesThickness = LOCATION_THICKNESS_FILTERS[user.workshopLocation];
    if (!matchesThickness) {
      return orders;
    }

    return orders.filter(order => {
      const orderData = ordersDetails[order.id];
      return orderData?.glassDetails?.some(detail => matchesThickness(detail.thickness));
    });
  };

  // Функция фильтрации заказов по поисковому запросу
  const getFilteredOrders = () => {
    const locationOrders = getLocationOrders();

    if (!searchTerm.trim()) return locationOrders;

    return locationOrders.filter(order => {
      const orderNumber = (order.orderNumber || order.id).toString().toLowerCase();
      const orderTitle = (order.title || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      // Поиск по номеру заказа и названию
      if (orderNumber.includes(searchLower) || orderTitle.includes(searchLower)) {
        return true;
      }
      
      // Поиск по деталям в заказе
      const orderData = ordersDetails[order.id];
      if (orderData && orderData.glassDetails) {
        return orderData.glassDetails.some(detail => {
          const originalText = (detail.originalText || '').toLowerCase();
          const description = (detail.description || '').toLowerCase();
          const glassType = (detail.glassType || '').toLowerCase();
          
          return originalText.includes(searchLower) || 
                 description.includes(searchLower) || 
                 glassType.includes(searchLower);
        });
      }
      
      return false;
    });
  };

  // Функция рендеринга деталировки стекла для конкретного заказа
  const renderGlassDetails = (orderId, orderNumber) => {
    const orderData = ordersDetails[orderId];
    const comments = ordersComments[orderId] || '';
    
    if (!orderData || !orderData.glassDetails || orderData.glassDetails.length === 0) {
      return (
        <div className="text-gray-500 text-sm">
          Деталировка не найдена или отсутствует
        </div>
      );
    }

    // Группируем детали по типу стекла
    const groupedDetails = {};
    orderData.glassDetails.forEach(detail => {
      const glassType = detail.glassType || 'Тип не указан';
      if (!groupedDetails[glassType]) {
        groupedDetails[glassType] = [];
      }
      groupedDetails[glassType].push(detail);
    });

    return (
      <div className="space-y-3">
        {/* Детали стекла сгруппированные по типу */}
        {Object.entries(groupedDetails).map(([glassType, details]) => (
          <div key={glassType} className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
              <h4 className="font-medium text-sm text-gray-800">{glassType}</h4>
            </div>
            <div className="p-2 space-y-1">
              {details.map((detail, index) => {
                const isSelected = orderData.selectedDetails.has(detail.id);
                const selectedQuantity = orderData.quantities.get(detail.id) || detail.quantity || 1;
                const maxQuantity = detail.quantity || 1;

                return (
                  <div key={detail.id || index} className={`p-2 border rounded transition-colors ${
                    isSelected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleDetailToggle(orderId, detail.id, detail)}
                          className="h-4 w-4 text-green-600 rounded"
                        />
                        <div className="text-sm flex items-center gap-2">
                          <span className="font-medium">{detail.originalText || detail.description || 'Описание отсутствует'}</span>
                          <span className="text-gray-600">-</span>
                          <span className="text-blue-600 font-medium">№{orderNumber}</span>
                          <SaveStatusIndicator orderId={orderId} detailId={detail.id} />
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600">Кол-во:</span>
                          <input
                            type="number"
                            min="1"
                            max={maxQuantity}
                            value={selectedQuantity}
                            onChange={(e) => handleQuantityChange(orderId, detail.id, parseInt(e.target.value) || 1, maxQuantity)}
                            className="w-12 px-1 py-1 border border-gray-300 rounded text-xs text-center"
                          />
                          <span className="text-xs text-gray-500">/{maxQuantity}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        {/* Статистика */}
        <div className="mt-3 p-2 bg-blue-50 rounded-lg">
          <div className="text-xs text-blue-700">
            Выбрано: {orderData.selectedDetails.size} из {orderData.glassDetails.length} деталей
            {orderData.selectedDetails.size > 0 && (
              <span className="ml-2">
                (Общее количество: {Array.from(orderData.quantities.values()).reduce((sum, qty) => sum + qty, 0)} шт)
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Функция рендеринга всех деталей из всех заказов (режим "По деталям")
  const renderDetailsOnly = (orders) => {
    const allDetails = [];
    
    orders.forEach(order => {
      const orderData = ordersDetails[order.id];
      if (orderData && orderData.glassDetails) {
        orderData.glassDetails.forEach(detail => {
          allDetails.push({
            ...detail,
            orderId: order.id,
            orderNumber: order.orderNumber || order.id,
            orderTitle: order.title,
            isSelected: orderData.selectedDetails.has(detail.id),
            selectedQuantity: orderData.quantities.get(detail.id) || detail.quantity || 1,
            maxQuantity: detail.quantity || 1
          });
        });
      }
    });

    if (allDetails.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          Детали не найдены
        </div>
      );
    }

    // Группируем детали по типу стекла
    const groupedDetails = {};
    allDetails.forEach(detail => {
      const glassType = detail.glassType || 'Тип не указан';
      if (!groupedDetails[glassType]) {
        groupedDetails[glassType] = [];
      }
      groupedDetails[glassType].push(detail);
    });

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-sm font-medium text-blue-800">
            Всего деталей: {allDetails.length} из {orders.length} заказов
          </div>
          <div className="text-xs text-blue-600">
            Выбрано: {allDetails.filter(d => d.isSelected).length} деталей
          </div>
        </div>
        
        {/* Детали сгруппированные по типу стекла */}
        {Object.entries(groupedDetails).map(([glassType, details]) => (
          <div key={glassType} className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
              <h4 className="font-medium text-sm text-gray-800">{glassType}</h4>
              <div className="text-xs text-gray-600">
                {details.length} деталей, выбрано: {details.filter(d => d.isSelected).length}
              </div>
            </div>
            <div className="p-3 space-y-2">
              {details.map((detail, index) => (
                <div key={`${detail.orderId}_${detail.id || index}`} className={`p-4 border rounded-lg transition-colors ${
                  detail.isSelected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={detail.isSelected}
                        onChange={() => handleDetailToggle(detail.orderId, detail.id, detail)}
                        className="h-5 w-5 text-green-600 rounded"
                      />
                      <div className="text-base flex items-center gap-2">
                        <span className="font-medium">{detail.originalText || detail.description || 'Описание отсутствует'}</span>
                        <span className="text-gray-600">-</span>
                        <span className="text-blue-600 font-medium">№{detail.orderNumber}</span>
                        <SaveStatusIndicator orderId={detail.orderId} detailId={detail.id} />
                      </div>
                    </div>
                    
                    {detail.isSelected && (
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600">Кол-во:</span>
                        <input
                          type="number"
                          min="1"
                          max={detail.maxQuantity}
                          value={detail.selectedQuantity}
                          onChange={(e) => handleQuantityChange(detail.orderId, detail.id, parseInt(e.target.value) || 1, detail.maxQuantity)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        />
                        <span className="text-sm text-gray-500">/{detail.maxQuantity}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="p-4">Загрузка заказов...</div>;

  const filteredOrders = getFilteredOrders();

  return (
    <div className="container mx-auto p-4">

      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-green-50 border-2 border-green-400 text-green-800 px-4 py-3 rounded-lg mb-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="text-lg">Поступило {newOrdersNotification.count} новых заказов!</strong>
                <div className="text-sm text-green-700 mt-1">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="ml-2 text-green-600 hover:text-green-800 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}


      {/* Переключатель вида */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-1">
          <span className="text-sm font-medium text-gray-700 mr-4">Вид:</span>
          <button
            onClick={() => setViewFilter("orders")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              viewFilter === "orders"
                ? "bg-blue-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            По заказам
          </button>
          <button
            onClick={() => setViewFilter("details")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              viewFilter === "details"
                ? "bg-blue-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            По деталям
          </button>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">
            {searchTerm ? 'Ничего не найдено' : 'Нет заказов'}
          </div>
          {searchTerm && (
            <div className="text-sm text-gray-400">
              Попробуйте изменить поисковый запрос
            </div>
          )}
        </div>
      ) : viewFilter === "orders" ? (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div 
              key={order.id} 
              className="bg-white p-4 border-2 border-gray-900 shadow-lg rounded-lg transform transition-all duration-300 hover:shadow-xl hover:border-blue-400"
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-medium">Тапсырыс №{order.orderNumber}</h2>
                  <p className="text-gray-600">{order.title}</p>
                  <p className="text-xs text-gray-500">Deal ID: {order.id}</p>
                </div>
              </div>

              {/* Чертежи для производства */}
              {order.productionFiles && order.productionFiles.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                  <div className="space-y-2">
                    {order.productionFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <PrintButton fileId={file.id} fileUrl={file.url} label="Печать" color="blue" />
                      </div>
                    ))}
                  </div>
                  <FilesContainer
                    files={order.productionFiles}
                    dealId={order.id}
                  />
                </div>
              )}

              {/* Деталировка к чертежу */}
              {order.files && order.files.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                  <div className="space-y-2">
                    {order.files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <PrintButton fileId={file.id} fileUrl={file.url} label="Печать" color="green" />
                      </div>
                    ))}
                  </div>
                  <FilesContainer
                    files={order.files}
                    dealId={order.id}
                  />
                </div>
              )}

              {/* Деталировка стекла (встроенная на странице) */}
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-3">
                  Деталировка сверления:
                </h3>
                {renderGlassDetails(order.id, order.orderNumber || order.id)}
              </div>

              {/* Свайп-кнопка во всю ширину блока */}
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={handleStageUpdate}
                  isLoading={processingOrders.has(order.id)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        renderDetailsOnly(filteredOrders)
      )}

    </div>
  );
};

export default DrillingOrdersPageB24;